import { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogOut, Users, MapPin, Clock, FileText, CheckCircle, XCircle, Bell, Plus, BarChart3, Send, Download, Settings, Award, DollarSign, TrendingUp, UserPlus, Trash2, Edit2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import EmployeeManagement from '@/components/EmployeeManagement';
import MapView from '@/components/MapView';
import AIInsights from '@/components/AIInsights';
import AttendanceReview from '@/components/AttendanceReview';
import { ResetOwnPassword } from '@/components/PasswordReset';
import PayrollManagement from '@/components/PayrollManagement';
import { ThemeToggle } from '@/context/ThemeContext';

export default function AdminDashboard() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [liveLocations, setLiveLocations] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [aiInsights, setAiInsights] = useState(null);
  
  // Dialog states
  const [groundDialogOpen, setGroundDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  
  // Form states
  const [groundForm, setGroundForm] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius_km: '50'
  });
  
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    assigned_ground_id: '',
    amc_value: '',
    amc_start_date: '',
    amc_end_date: ''
  });
  
  const [broadcastMessage, setBroadcastMessage] = useState('');

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes, leavesRes, locationsRes, groundsRes, clientsRes, insightsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/pending-users`, { headers }),
        axios.get(`${API}/leaves/pending`, { headers }),
        axios.get(`${API}/attendance/live-locations`, { headers }),
        axios.get(`${API}/grounds`, { headers }),
        axios.get(`${API}/clients`, { headers }),
        axios.get(`${API}/admin/ai-insights`, { headers })
      ]);

      setStats(statsRes.data);
      setPendingUsers(usersRes.data);
      setPendingLeaves(leavesRes.data);
      setLiveLocations(locationsRes.data);
      setGrounds(groundsRes.data);
      setClients(clientsRes.data);
      setAiInsights(insightsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/approve-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User approved successfully');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    try {
      await axios.post(`${API}/leaves/${leaveId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Leave ${action}d successfully`);
      fetchDashboardData();
    } catch (error) {
      toast.error(`Failed to ${action} leave`);
    }
  };
  
  const handleCreateGround = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/grounds`, {
        ...groundForm,
        latitude: parseFloat(groundForm.latitude),
        longitude: parseFloat(groundForm.longitude),
        radius_km: parseFloat(groundForm.radius_km)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Ground created successfully');
      setGroundDialogOpen(false);
      setGroundForm({ name: '', address: '', latitude: '', longitude: '', radius_km: '50' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create ground');
    }
  };
  
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients`, {
        ...clientForm,
        amc_value: parseFloat(clientForm.amc_value)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Client created successfully');
      setClientDialogOpen(false);
      setClientForm({ name: '', email: '', phone: '', assigned_ground_id: '', amc_value: '', amc_start_date: '', amc_end_date: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create client');
    }
  };
  
  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    
    toast.success(`📢 Broadcast message sent to all employees!\n"${broadcastMessage}"`);
    setBroadcastMessage('');
    setBroadcastDialogOpen(false);
  };
  
  const handleDeleteGround = async (groundId) => {
    if (!window.confirm('Are you sure you want to delete this ground?')) return;
    
    try {
      const response = await axios.delete(`${API}/grounds/${groundId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ground deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Delete ground error:', error.response || error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete ground';
      toast.error(errorMsg);
    }
  };
  
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      const response = await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Delete client error:', error.response || error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete client';
      toast.error(errorMsg);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600" style={{fontFamily: 'Inter, sans-serif'}}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const attendanceData = [
    { day: 'Mon', present: 35, absent: 5, late: 2 },
    { day: 'Tue', present: 38, absent: 2, late: 1 },
    { day: 'Wed', present: 36, absent: 4, late: 3 },
    { day: 'Thu', present: 39, absent: 1, late: 1 },
    { day: 'Fri', present: 37, absent: 3, late: 2 },
    { day: 'Sat', present: 40, absent: 0, late: 0 },
  ];
  
  const performanceData = [
    { name: 'Excellent', value: 15, color: '#10b981' },
    { name: 'Good', value: 20, color: '#3b82f6' },
    { name: 'Average', value: 10, color: '#f59e0b' },
    { name: 'Needs Improvement', value: 5, color: '#ef4444' },
  ];
  
  const salaryTrend = [
    { month: 'Jan', amount: 580000 },
    { month: 'Feb', amount: 620000 },
    { month: 'Mar', amount: 590000 },
    { month: 'Apr', amount: 630000 },
    { month: 'May', amount: 650000 },
    { month: 'Jun', amount: 630000 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50" data-testid="admin-dashboard">
      {/* Enhanced Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>TurfSense Admin</h1>
                <p className="text-sm text-gray-600" style={{fontFamily: 'Inter, sans-serif'}}>Welcome back, {user.full_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <ResetOwnPassword token={token} />
              
              <Dialog open={broadcastDialogOpen} onOpenChange={setBroadcastDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="broadcast-btn">
                    <Send className="h-4 w-4 mr-2" />
                    Broadcast
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Broadcast Message to All Employees</DialogTitle>
                    <DialogDescription>Send an instant notification to all active employees</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Type your message here..."
                      value={broadcastMessage}
                      onChange={(e) => setBroadcastMessage(e.target.value)}
                      rows={4}
                      data-testid="broadcast-message"
                    />
                    <Button onClick={handleBroadcast} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="send-broadcast-btn">
                      <Send className="h-4 w-4 mr-2" />
                      Send Broadcast
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Enhanced KPIs with Trends */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={<Users className="h-7 w-7 text-blue-600" />}
            title="Total Employees"
            value={stats?.total_employees || 0}
            trend="+5%"
            trendUp={true}
            bgColor="bg-gradient-to-br from-blue-50 to-blue-100"
            iconBg="bg-blue-100"
          />
          <StatCard
            icon={<CheckCircle className="h-7 w-7 text-emerald-600" />}
            title="Present Today"
            value={stats?.present_today || 0}
            trend="95%"
            trendUp={true}
            bgColor="bg-gradient-to-br from-emerald-50 to-emerald-100"
            iconBg="bg-emerald-100"
          />
          <StatCard
            icon={<MapPin className="h-7 w-7 text-purple-600" />}
            title="Active Grounds"
            value={stats?.total_grounds || 0}
            trend="+2 new"
            trendUp={true}
            bgColor="bg-gradient-to-br from-purple-50 to-purple-100"
            iconBg="bg-purple-100"
          />
          <StatCard
            icon={<DollarSign className="h-7 w-7 text-orange-600" />}
            title="Monthly Payroll"
            value="₹6.3L"
            trend="+8%"
            trendUp={true}
            bgColor="bg-gradient-to-br from-orange-50 to-orange-100"
            iconBg="bg-orange-100"
          />
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 h-auto p-1">
            <TabsTrigger value="overview" className="py-3" data-testid="overview-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="manage" className="py-3" data-testid="manage-tab">
              <Settings className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Manage</span>
            </TabsTrigger>
            <TabsTrigger value="approvals" className="py-3" data-testid="approvals-tab">
              <Bell className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Approvals</span>
              {(pendingUsers.length + pendingLeaves.length) > 0 && (
                <Badge className="ml-2 bg-red-500">{pendingUsers.length + pendingLeaves.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="employees" className="py-3" data-testid="employees-tab">
              <Users className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Employees</span>
            </TabsTrigger>
            <TabsTrigger value="locations" className="py-3" data-testid="locations-tab">
              <MapPin className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Live Map</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="py-3" data-testid="reports-tab">
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Reports</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <TrendingUp className="h-5 w-5 mr-2 text-emerald-600" />
                  Weekly Attendance Trend
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={attendanceData}>
                    <defs>
                      <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip />
                    <Area type="monotone" dataKey="present" stroke="#10b981" fillOpacity={1} fill="url(#colorPresent)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <Award className="h-5 w-5 mr-2 text-purple-600" />
                  Employee Performance
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={performanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {performanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                  Attendance Breakdown
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={attendanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" fill="#10b981" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="absent" fill="#ef4444" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="late" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <DollarSign className="h-5 w-5 mr-2 text-orange-600" />
                  Monthly Payroll Trend
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={salaryTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip formatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
                    <Line type="monotone" dataKey="amount" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </TabsContent>

          {/* Manage Tab */}
          <TabsContent value="manage" className="mt-6 space-y-6">
            {/* Employee Management - Full Width */}
            <EmployeeManagement token={token} />
            
            {/* Payroll Management - Full Width */}
            <PayrollManagement token={token} />
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Manage Grounds */}
              <Card className="p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    <MapPin className="h-5 w-5 mr-2 text-purple-600" />
                    Grounds Management
                  </h3>
                  <Dialog open={groundDialogOpen} onOpenChange={setGroundDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="add-ground-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add Ground
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add New Ground</DialogTitle>
                        <DialogDescription>Create a new sports ground location</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateGround} className="space-y-4">
                        <div>
                          <Label>Ground Name</Label>
                          <Input
                            placeholder="Central Sports Complex"
                            value={groundForm.name}
                            onChange={(e) => setGroundForm({...groundForm, name: e.target.value})}
                            required
                            data-testid="ground-name"
                          />
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input
                            placeholder="Mumbai, Maharashtra"
                            value={groundForm.address}
                            onChange={(e) => setGroundForm({...groundForm, address: e.target.value})}
                            required
                            data-testid="ground-address"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Latitude</Label>
                            <Input
                              type="number"
                              step="any"
                              placeholder="19.0760"
                              value={groundForm.latitude}
                              onChange={(e) => setGroundForm({...groundForm, latitude: e.target.value})}
                              required
                              data-testid="ground-latitude"
                            />
                          </div>
                          <div>
                            <Label>Longitude</Label>
                            <Input
                              type="number"
                              step="any"
                              placeholder="72.8777"
                              value={groundForm.longitude}
                              onChange={(e) => setGroundForm({...groundForm, longitude: e.target.value})}
                              required
                              data-testid="ground-longitude"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Radius (km)</Label>
                          <Input
                            type="number"
                            placeholder="50"
                            value={groundForm.radius_km}
                            onChange={(e) => setGroundForm({...groundForm, radius_km: e.target.value})}
                            required
                            data-testid="ground-radius"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" data-testid="create-ground-btn">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Ground
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <ScrollArea className="h-80">
                  <div className="space-y-3">
                    {grounds.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No grounds yet</p>
                    ) : (
                      grounds.map((ground) => (
                        <div key={ground.id} className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800">{ground.name}</p>
                              <p className="text-sm text-gray-600 mt-1">{ground.address}</p>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs text-gray-500">📍 {ground.latitude.toFixed(4)}, {ground.longitude.toFixed(4)}</span>
                                <Badge variant="outline" className="text-xs">Radius: {ground.radius_km}km</Badge>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteGround(ground.id)}
                              data-testid={`delete-ground-${ground.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </Card>

              {/* Manage Clients */}
              <Card className="p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    <UserPlus className="h-5 w-5 mr-2 text-orange-600" />
                    Client Management
                  </h3>
                  <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700" data-testid="add-client-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add Client
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add New Client</DialogTitle>
                        <DialogDescription>Register a new client with AMC details</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateClient} className="space-y-4">
                        <div>
                          <Label>Client Name</Label>
                          <Input
                            placeholder="ABC Sports Club"
                            value={clientForm.name}
                            onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                            required
                            data-testid="client-name"
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            placeholder="client@example.com"
                            value={clientForm.email}
                            onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                            required
                            data-testid="client-email"
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            placeholder="+919876543210"
                            value={clientForm.phone}
                            onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                            required
                            data-testid="client-phone"
                          />
                        </div>
                        <div>
                          <Label>Assigned Ground</Label>
                          <Select value={clientForm.assigned_ground_id} onValueChange={(value) => setClientForm({...clientForm, assigned_ground_id: value})}>
                            <SelectTrigger data-testid="client-ground-select">
                              <SelectValue placeholder="Select ground" />
                            </SelectTrigger>
                            <SelectContent>
                              {grounds.map((g) => (
                                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>AMC Value (₹)</Label>
                          <Input
                            type="number"
                            placeholder="500000"
                            value={clientForm.amc_value}
                            onChange={(e) => setClientForm({...clientForm, amc_value: e.target.value})}
                            required
                            data-testid="client-amc"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={clientForm.amc_start_date}
                              onChange={(e) => setClientForm({...clientForm, amc_start_date: e.target.value})}
                              required
                              data-testid="client-start-date"
                            />
                          </div>
                          <div>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={clientForm.amc_end_date}
                              onChange={(e) => setClientForm({...clientForm, amc_end_date: e.target.value})}
                              required
                              data-testid="client-end-date"
                            />
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" data-testid="create-client-btn">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Client
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <ScrollArea className="h-80">
                  <div className="space-y-3">
                    {clients.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No clients yet</p>
                    ) : (
                      clients.map((client) => (
                        <div key={client.id} className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800">{client.name}</p>
                              <p className="text-sm text-gray-600">{client.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-500">Active</Badge>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteClient(client.id)}
                                data-testid={`delete-client-${client.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <Separator className="my-2" />
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">AMC Value</span>
                            <span className="font-semibold text-orange-600">₹{client.amc_value.toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">{client.amc_start_date} to {client.amc_end_date}</p>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </div>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="mt-6 space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
                  Pending User Approvals
                  {pendingUsers.length > 0 && <Badge className="ml-2 bg-red-500">{pendingUsers.length}</Badge>}
                </h3>
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No pending approvals</p>
                  </div>
                ) : (
                  <ScrollArea className="h-96">
                    <div className="space-y-4">
                      {pendingUsers.map((u) => (
                        <div key={u.id} className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200" data-testid={`pending-user-${u.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800">{u.full_name}</p>
                              <p className="text-sm text-gray-600">{u.email}</p>
                              <p className="text-sm text-gray-600">{u.phone}</p>
                              <Badge className="mt-2" variant="outline">{u.role}</Badge>
                            </div>
                            {u.face_image && (
                              <img src={u.face_image} alt="Face" className="w-16 h-16 rounded-lg object-cover" />
                            )}
                          </div>
                          <Button 
                            onClick={() => handleApproveUser(u.id)} 
                            size="sm" 
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-user-btn-${u.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve User
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </Card>

              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <FileText className="h-5 w-5 mr-2 text-purple-600" />
                  Pending Leave Requests
                  {pendingLeaves.length > 0 && <Badge className="ml-2 bg-red-500">{pendingLeaves.length}</Badge>}
                </h3>
                {pendingLeaves.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No pending leave requests</p>
                  </div>
                ) : (
                  <ScrollArea className="h-96">
                    <div className="space-y-4">
                      {pendingLeaves.map((leave) => (
                        <div key={leave.id} className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200" data-testid={`leave-${leave.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800">{leave.user_name}</p>
                              <Badge className="mt-1" variant="outline">{leave.leave_type}</Badge>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{leave.reason}</p>
                          <p className="text-xs text-gray-500 mb-3">{leave.start_date} to {leave.end_date}</p>
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => handleLeaveAction(leave.id, 'approve')} 
                              size="sm" 
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`approve-leave-btn-${leave.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              onClick={() => handleLeaveAction(leave.id, 'reject')} 
                              size="sm" 
                              variant="destructive"
                              className="flex-1"
                              data-testid={`reject-leave-btn-${leave.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="mt-6">
            {/* Attendance Review with Dual Authentication */}
            <AttendanceReview attendance={stats?.recent_attendance || []} />
          </TabsContent>

          {/* Live Locations Tab */}
          <TabsContent value="locations" className="mt-6">
            {/* Google Maps View */}
            <MapView grounds={grounds} liveLocations={liveLocations} />
            
            {/* List View Below Map */}
            <Card className="p-6 shadow-lg mt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <MapPin className="h-5 w-5 mr-2 text-purple-600" />
                Employee Location Details
                <Badge className="ml-2 bg-green-500">{liveLocations.length} Active</Badge>
              </h3>
              {liveLocations.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No active check-ins</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {liveLocations.map((loc) => (
                    <div key={loc.id} className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200" data-testid={`location-${loc.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-800">{loc.user_name}</p>
                          <Badge className="mt-1 bg-green-500">Live</Badge>
                        </div>
                        <MapPin className="h-5 w-5 text-emerald-600" />
                      </div>
                      <Separator className="my-2" />
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ground:</span>
                          <span className="font-medium">{loc.ground_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Latitude:</span>
                          <span className="font-mono">{loc.latitude.toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Longitude:</span>
                          <span className="font-mono">{loc.longitude.toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Since:</span>
                          <span className="font-medium">{new Date(loc.check_in_time).toLocaleTimeString()}</span>
                        </div>
                        {loc.distance_from_ground && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Distance:</span>
                            <span className="font-medium text-green-600">{loc.distance_from_ground} km</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <Download className="h-5 w-5 mr-2 text-blue-600" />
                  Generate Reports
                </h3>
                <div className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" data-testid="export-attendance-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Export Attendance Report (Excel)
                  </Button>
                  <Button className="w-full justify-start" variant="outline" data-testid="export-payroll-btn">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Export Payroll Report (PDF)
                  </Button>
                  <Button className="w-full justify-start" variant="outline" data-testid="export-leave-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Export Leave Report (Excel)
                  </Button>
                  <Button className="w-full justify-start" variant="outline" data-testid="export-client-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Export Client Report (PDF)
                  </Button>
                </div>
              </Card>

              <Card className="p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <BarChart3 className="h-5 w-5 mr-2 text-purple-600" />
                  Quick Insights
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Average Attendance Rate</p>
                    <p className="text-2xl font-bold text-blue-600">92.5%</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Total Working Hours (Month)</p>
                    <p className="text-2xl font-bold text-emerald-600">8,420 hrs</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Pending Leave Days</p>
                    <p className="text-2xl font-bold text-orange-600">45 days</p>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* AI Insights Section */}
            <AIInsights insights={aiInsights?.insights || []} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon, title, value, trend, trendUp, bgColor, iconBg }) {
  return (
    <Card className={`p-6 ${bgColor} border-none shadow-lg hover:shadow-xl transition-shadow`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`${iconBg} w-14 h-14 rounded-xl flex items-center justify-center shadow-md`}>
          {icon}
        </div>
        {trend && (
          <Badge className={trendUp ? 'bg-green-500' : 'bg-red-500'}>
            {trendUp ? '↗' : '↘'} {trend}
          </Badge>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-1" style={{fontFamily: 'Inter, sans-serif'}}>{title}</p>
      <p className="text-3xl font-bold text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{value}</p>
    </Card>
  );
}